/**
 * Assets cell classification against Constitution §13.2 provenance classes.
 *
 * - Observed: the value matches what the source system reported (VehicleObserved).
 * - Asserted: the value was entered on the Assets tab and differs from it.
 * - Unknown: no value, and none substituted.
 *
 * "Confirmed" is an additional review attribute on an Asserted value (a user
 * re-checked it). It maps onto Asserted and never replaces it (§13.2), so the
 * UI must always label it "Asserted · confirmed", not as its own class.
 */

export type AssetFieldConfirmations = Record<string, string>;

/** Fields that can be asserted / confirmed on the Assets roster. */
export const CONFIRMABLE_VEHICLE_FIELDS = [
  'type',
  'service_life_yr',
  'start_year',
  'circuit_name',
  'fuel',
  'typical_tonnes',
  'unit_cost',
  'insurance_yr',
  'acquisition',
  'trailer_deck',
  'tail_lift',
  'adr_equipped',
  'moffett',
  'small_vehicle',
  'pallet_spaces',
] as const;

export type ConfirmableVehicleField = (typeof CONFIRMABLE_VEHICLE_FIELDS)[number];

/** Empty / em-dash treat as no value. */
export function assetFieldNorm(value: unknown): string {
  const s = String(value ?? '').trim();
  return !s || s === '—' ? '' : s;
}

export function assetConfirmKey(registration: string, field: string): string {
  const reg = String(registration ?? '').replace(/\s+/g, '').toUpperCase();
  return `${reg}.${field}`;
}

/** §13.2 provenance class of an Assets cell, plus the confirmed review attribute. */
export type AssetFieldStatus = 'observed' | 'asserted' | 'confirmed' | 'unknown';

export function resolveAssetFieldStatus(
  current: unknown,
  observed: unknown,
  confirmed?: unknown,
): AssetFieldStatus {
  const cur = assetFieldNorm(current);
  if (!cur) return 'unknown';
  if (cur === assetFieldNorm(observed)) return 'observed';
  if (assetFieldNorm(confirmed) !== '' && cur === assetFieldNorm(confirmed)) {
    return 'confirmed';
  }
  return 'asserted';
}

export function assetFieldTextClass(status: AssetFieldStatus): string {
  if (status === 'confirmed') return 'text-success font-semibold';
  if (status === 'asserted') return 'text-ring font-semibold';
  if (status === 'unknown') return 'text-muted-foreground font-normal';
  return 'text-foreground font-normal';
}

export function assetFieldInputClass(status: AssetFieldStatus, baseInputClass: string): string {
  if (status === 'confirmed') return `${baseInputClass} border-success/70 text-success`;
  if (status === 'asserted') return `${baseInputClass} border-ring/70 text-ring`;
  return `${baseInputClass} border-accent text-foreground`;
}

export function assetFieldStatusTitle(status: AssetFieldStatus): string {
  if (status === 'confirmed') return 'Asserted · confirmed — entered here and re-checked by a user';
  if (status === 'asserted') return 'Asserted — entered here, differs from the source system';
  if (status === 'unknown') return 'Unknown — no value recorded';
  return 'Observed — as reported by the source system';
}

/** Collect Asserted (non-observed) field values from a saved Assets roster snapshot. */
export function collectAssertedAssetFieldOverrides(args: {
  rows: Array<Record<string, string | number>>;
  observedByReg: Map<string, Record<string, string | number>>;
  fields?: readonly ConfirmableVehicleField[];
}): AssetFieldConfirmations {
  const fields = args.fields ?? CONFIRMABLE_VEHICLE_FIELDS;
  const overrides: AssetFieldConfirmations = {};
  for (const row of args.rows) {
    const reg = String(row.vehicle_reg ?? '').trim();
    if (!reg) continue;
    const observed = args.observedByReg.get(reg);
    for (const field of fields) {
      if (assetFieldNorm(row[field]) === assetFieldNorm(observed?.[field])) continue;
      const val = assetFieldNorm(row[field]);
      if (!val) continue;
      overrides[assetConfirmKey(reg, field)] = val;
    }
  }
  return overrides;
}

export function hasUnconfirmedAssertedOverrides(args: {
  overrides: AssetFieldConfirmations;
  confirmations: AssetFieldConfirmations;
}): boolean {
  for (const [key, value] of Object.entries(args.overrides)) {
    const stamped = args.confirmations[key];
    if (stamped == null || assetFieldNorm(stamped) !== assetFieldNorm(value)) return true;
  }
  return false;
}
